import React, { useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Stack,
  Autocomplete
} from '@mui/material';
import { Racquet } from '../types';
import { useScrollIntoView } from '../hooks/useScrollIntoView';

interface Props {
  racquets: Racquet[];
  racquetId: string;
  onSelectRacquet: (id: string) => void;

  isAddingRacquet: boolean;
  onAddRacquetToggle: (adding: boolean) => void;

  newRacquetData: { brand: string; model: string };
  onChangeNewRacquet: (data: { brand: string; model: string }) => void;
}

export default function StepRacquetForm({
  racquets,
  racquetId,
  onSelectRacquet,
  isAddingRacquet,
  onAddRacquetToggle,
  newRacquetData,
  onChangeNewRacquet,
}: Props) {
  const scrollIntoView = useScrollIntoView();
  const autocompleteRef = useRef<HTMLDivElement>(null);

  const allOptions = [
    { id: 'add-racquet', brand: '', model: '+ Add new racquet' },
    ...racquets,
  ];

  const handleChange = (_e: any, value: Racquet | null) => {
    if (!value) return;

    if (value.id === 'add-racquet') {
      onAddRacquetToggle(true);
      onSelectRacquet('add-racquet');
    } else {
      onAddRacquetToggle(false);
      onSelectRacquet(value.id);
    }
  };

  const handleAutocompleteOpen = () => {
    setTimeout(() => {
      const input = autocompleteRef.current?.querySelector('input') || document.activeElement as HTMLInputElement;
      if (input && input instanceof HTMLInputElement) {
        scrollIntoView(input);
      }
    }, 100);
  };

  const handleTextFieldFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    scrollIntoView(e.target);
  };

  return (
    <Box mt={2}>
      <Autocomplete
        ref={autocompleteRef}
        options={allOptions}
        value={racquets.find(r => r.id === racquetId) || null}
        getOptionLabel={(option) =>
          option.id === 'add-racquet'
            ? option.model
            : `${option.brand} ${option.model}`
        }
        renderOption={(props, option) => {
          const label =
            option.id === 'add-racquet'
              ? option.model
              : `${option.brand} ${option.model}`;
          return (
            <li {...props}>
              <Box
                component="span"
                sx={{ fontStyle: option.id === 'add-racquet' ? 'italic' : 'normal' }}
              >
                {label}
              </Box>
            </li>
          );
        }}
        filterOptions={(options, { inputValue }) => {
          const filtered = options.filter((r) => {
            if (r.id === 'add-racquet') return true; // Always show Add New
            const label = `${r.brand} ${r.model}`.toLowerCase();
            return label.includes(inputValue.toLowerCase());
          });
        
          // Ensure Add New is always first:
          const addNew = options.find(o => o.id === 'add-racquet');
          return [addNew!, ...filtered.filter(o => o.id !== 'add-racquet')];
        }}
        renderInput={(params) => (
          <TextField {...params} label="Racquet" placeholder="Select or search racquet" />
        )}
        onChange={handleChange}
        onOpen={handleAutocompleteOpen}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />

      {isAddingRacquet && (
        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Brand"
                value={newRacquetData.brand}
                onChange={(e) =>
                  onChangeNewRacquet({ ...newRacquetData, brand: e.target.value })
                }
                onFocus={handleTextFieldFocus}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Model"
                value={newRacquetData.model}
                onChange={(e) =>
                  onChangeNewRacquet({ ...newRacquetData, model: e.target.value })
                }
                onFocus={handleTextFieldFocus}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
}
